/**
 * TopBar — V3 thin top bar above page content (not above sidebar).
 *
 * Spec
 * ----
 *  - Height 48 px, white surface, 1 px `#E8E8E8` bottom border.
 *  - Left (RTL: right): page-name breadcrumb in 14 px primary text.
 *  - Right (RTL: left): EN/AR language toggle, search icon, notification
 *    bell, 30 px square avatar.
 */
import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Bell, PlayCircle, LogOut, Settings as SettingsIcon } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTourState } from '@/components/onboarding/useTourState';
import { cn } from '@/lib/utils';

function LanguageToggle() {
  const { language, setLanguage } = useLanguage();
  const isAr = language === 'ar';
  const segWidth = 36;

  return (
    <div
      dir="ltr"
      className="relative flex items-center"
      style={{
        background: 'var(--canvas)',
        border: 'var(--border-default)',
        borderRadius: '9999px',
        padding: '2px',
        height: '28px',
      }}
      data-testid="lang-toggle"
    >
      {/* Sliding selector */}
      <span
        aria-hidden="true"
        className="absolute top-0.5 bottom-0.5 transition-all duration-200"
        style={{
          width: `${segWidth}px`,
          background: '#111111',
          borderRadius: '9999px',
          left: isAr ? `${segWidth + 2}px` : '2px',
        }}
      />
      <button
        type="button"
        onClick={() => setLanguage('en')}
        className="relative z-10 text-[11px] font-medium transition-colors"
        style={{
          width: `${segWidth}px`,
          color: !isAr ? '#FFFFFF' : 'var(--text-secondary)',
        }}
        data-testid="lang-toggle-en"
      >
        EN
      </button>
      <button
        type="button"
        onClick={() => setLanguage('ar')}
        className="relative z-10 text-[11px] font-medium transition-colors"
        style={{
          width: `${segWidth}px`,
          color: isAr ? '#FFFFFF' : 'var(--text-secondary)',
        }}
        data-testid="lang-toggle-ar"
      >
        AR
      </button>
    </div>
  );
}

export function TopBar({ breadcrumb, showMenuButton = false, onMenuToggle }) {
  const { language } = useLanguage();
  const { user, signOut } = useAuth();
  const isArabic = language === 'ar';
  const navigate = useNavigate();

  const userName = user?.name || user?.email?.split('@')[0] || (isArabic ? 'الزائر' : 'Guest');
  const userInitials = (userName || '?').slice(0, 2).toUpperCase();

  // Cluster J.D (Feb 2026) — user-menu dropdown w/ replay-tour entry.
  // Avatar was a non-interactive `<div aria-hidden>`; now a button
  // that toggles a small dropdown. Outside-click closes it via the
  // overlay backdrop pattern (no extra hook).
  const { replay: replayTour, authReady: tourReady } = useTourState();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const handleReplayTour = () => {
    setMenuOpen(false);
    replayTour();
    navigate('/chat');
  };
  const handleSignOut = async () => {
    setMenuOpen(false);
    try { await signOut?.(); } catch (_e) { /* surfaced via toast in AuthContext */ }
  };

  return (
    <header
      dir={isArabic ? 'rtl' : 'ltr'}
      className="h-[48px] shrink-0 flex items-center justify-between px-5"
      style={{
        background: 'var(--surface)',
        borderBottom: 'var(--border-default)',
      }}
      data-testid="v3-top-bar"
    >
      <div className={cn('flex items-center gap-3', isArabic && 'flex-row-reverse')}>
        {showMenuButton && (
          <button
            type="button"
            onClick={onMenuToggle}
            aria-label={isArabic ? 'القائمة' : 'Menu'}
            className="flex items-center justify-center hover:bg-black/5 transition rounded-md"
            style={{ width: '36px', height: '36px', border: 'none', background: 'transparent', cursor: 'pointer' }}
            data-testid="v3-top-bar-menu-toggle"
          >
            <svg width="18" height="14" viewBox="0 0 18 14" fill="none" aria-hidden="true">
              <rect width="18" height="2" rx="1" fill="#111111" />
              <rect y="6" width="18" height="2" rx="1" fill="#111111" />
              <rect y="12" width="18" height="2" rx="1" fill="#111111" />
            </svg>
          </button>
        )}
        <div
          className={cn('text-[14px]', isArabic && 'text-right')}
          style={{ color: 'var(--text-primary)' }}
          data-testid="v3-top-bar-breadcrumb"
        >
          {breadcrumb}
        </div>
      </div>

      <div className={cn('flex items-center gap-2', isArabic && 'flex-row-reverse')}>
        <LanguageToggle />
        <button
          type="button"
          aria-label={isArabic ? 'بحث' : 'Search'}
          className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-black/5 transition"
          data-testid="v3-top-bar-search"
        >
          <Search className="w-4 h-4" strokeWidth={1.7} style={{ color: 'var(--text-secondary)' }} />
        </button>
        <button
          type="button"
          aria-label={isArabic ? 'الإشعارات' : 'Notifications'}
          className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-black/5 transition"
          data-testid="v3-top-bar-notifications"
        >
          <Bell className="w-4 h-4" strokeWidth={1.7} style={{ color: 'var(--text-secondary)' }} />
        </button>
        <div className="relative">
          <button
            type="button"
            aria-label={isArabic ? 'قائمة المستخدم' : 'User menu'}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            className="w-[30px] h-[30px] flex items-center justify-center text-[11px] font-medium text-white transition hover:opacity-90"
            style={{ background: '#111111', borderRadius: 'var(--radius-avatar)', border: 'none', cursor: 'pointer' }}
            data-testid="v3-top-bar-avatar"
          >
            {userInitials}
          </button>
          {menuOpen && (
            <>
              {/* Backdrop for outside-click close — covers the page,
                  intercepts pointer events so dropdown closes cleanly
                  without a global listener. */}
              <div
                onClick={() => setMenuOpen(false)}
                className="fixed inset-0"
                style={{ zIndex: 'var(--z-tooltips, 50)', background: 'transparent' }}
                aria-hidden="true"
                data-testid="topbar-user-menu-backdrop"
              />
              <div
                role="menu"
                dir={isArabic ? 'rtl' : 'ltr'}
                className="absolute mt-2 w-56 rounded-md shadow-lg overflow-hidden"
                style={{
                  background: 'var(--surface)',
                  border: 'var(--border-default)',
                  zIndex: 'var(--z-tooltips, 50)',
                  top: '100%',
                  ...(isArabic ? { left: 0 } : { right: 0 }),
                }}
                data-testid="topbar-user-menu"
              >
                <div
                  className={cn('px-3 py-2 text-[12px]', isArabic && 'text-right')}
                  style={{ color: 'var(--text-secondary)', borderBottom: 'var(--border-default)' }}
                >
                  {user?.email || userName}
                </div>
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleReplayTour}
                  disabled={!tourReady}
                  className={cn(
                    'w-full px-3 py-2 text-[13px] flex items-center gap-2 hover:bg-black/5 disabled:opacity-50',
                    isArabic && 'flex-row-reverse text-right',
                  )}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-primary)' }}
                  data-testid="topbar-replay-tour"
                >
                  <PlayCircle className="w-4 h-4" />
                  {isArabic ? 'إعادة الجولة التعريفية' : 'Take the tour again'}
                </button>
                <Link
                  to="/settings"
                  role="menuitem"
                  onClick={() => setMenuOpen(false)}
                  className={cn(
                    'w-full px-3 py-2 text-[13px] flex items-center gap-2 hover:bg-black/5',
                    isArabic && 'flex-row-reverse text-right',
                  )}
                  style={{ color: 'var(--text-primary)', textDecoration: 'none' }}
                  data-testid="topbar-settings-link"
                >
                  <SettingsIcon className="w-4 h-4" />
                  {isArabic ? 'الإعدادات' : 'Settings'}
                </Link>
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleSignOut}
                  className={cn(
                    'w-full px-3 py-2 text-[13px] flex items-center gap-2 hover:bg-black/5',
                    isArabic && 'flex-row-reverse text-right',
                  )}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', borderTop: 'var(--border-default)' }}
                  data-testid="topbar-signout"
                >
                  <LogOut className="w-4 h-4" />
                  {isArabic ? 'تسجيل الخروج' : 'Sign out'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export default TopBar;
